import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Search, Shield, BarChart3, Globe, Users, Heart, ArrowUpLeft } from "lucide-react";
import { APP_NAME } from "@/lib/constants";
import { SEO } from "@/components/SEO";

const About = () => {
  return (
    <Layout>
      <SEO 
        title="אודות" 
        description={`${APP_NAME} היא פלטפורמה המחברת בין יוצרים שבונים מערכות חכמות לבין משתמשים שמחפשים פתרונות דיגיטליים יעילים — לכל מטרה.`} 
      />
      <div className="bg-white min-h-screen pb-20">

        {/* Hero */}
        <section className="pt-32 pb-20 border-b border-slate-100">
          <div className="container mx-auto px-6 lg:px-12 text-center max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-6">
              המקום שבו<br />
              <span className="text-slate-500">חדשנות פוגשת צורך.</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto">
              {APP_NAME} היא פלטפורמה המחברת בין יוצרים שבונים מערכות חכמות לבין משתמשים שמחפשים פתרונות דיגיטליים יעילים — לכל מטרה.
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-24">
          <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <div className="text-sm font-semibold text-blue-600 tracking-wide">המשימה שלנו</div>
                <h2 className="text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                  במה לכל יוצר,<br />פתרון לכל משתמש.
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  העולם מלא באנשים מוכשרים שיוצרים מערכות מדהימות בעזרת כלי AI מתקדמים. לעיתים קרובות, הפתרונות הללו נשארים במגירה או מגיעים לקהל מצומצם.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  {APP_NAME} נוצרה כדי לתת במה למערכות האלו. אנחנו מאפשרים לכל אחד לפרסם את היצירה שלו, לקבל פידבק אמיתי ולצמוח. למשתמשים, אנחנו מציעים גישה למאגר עצום של פתרונות שאי אפשר למצוא בשום מקום אחר.
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-10 border border-slate-100 space-y-6">
                {[
                  { value: "100%", label: "תוכן גולשים" },
                  { value: "24/7", label: "מרקטפלייס פתוח" },
                  { value: "0 ₪", label: "עלות פרסום" },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-baseline justify-between border-b border-slate-200/60 pb-4 last:border-0 last:pb-0">
                    <span className="text-2xl font-bold text-slate-900">{item.value}</span>
                    <span className="text-sm text-slate-500 font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-slate-50/50 border-t border-b border-slate-100">
          <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
                הערכים שלנו
              </h2>
              <p className="text-slate-500 mt-3 text-base">
                העקרונות שמובילים את הקהילה שלנו.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                {
                  icon: Users,
                  title: "פתיחות",
                  desc: "הפלטפורמה פתוחה לכולם. כל אחד יכול לפרסם מערכת, וכל אחד יכול להשתמש ולדרג. ללא חסמים."
                },
                {
                  icon: Shield,
                  title: "אמינות",
                  desc: "הביקורות באתר מגיעות ממשתמשים אמיתיים. אנחנו משקיעים משאבים רבים כדי לוודא שהמידע אמין ושקוף."
                },
                {
                  icon: Heart,
                  title: "חדשנות",
                  desc: "אנחנו חוגגים יצירתיות. המטרה שלנו היא לעודד אנשים ליצור ולשתף פתרונות חדשים."
                }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border border-slate-200 mx-auto mb-5 text-slate-900">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-slate-900">{item.title}</h3>
                  <p className="text-slate-500 leading-relaxed text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* For who */}
        <section className="py-24">
          <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
                למי זה מיועד?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: Search,
                  title: "למשתמשים וגולשים",
                  desc: "מחפשים כלי לניהול משימות? מערכת למידה? או אולי פתרון יצירתי לתחביב? כאן תמצאו מגוון עצום של מערכות לכל צורך.",
                  cta: "חפש פתרון",
                  link: "/systems"
                },
                {
                  icon: BarChart3,
                  title: "ליוצרים ומפתחים",
                  desc: "בניתם מערכת חכמה? אל תשאירו אותה לעצמכם. פרסמו אותה כאן, קבלו חשיפה ובנו לעצמכם שם בתחום.",
                  cta: "פרסם יצירה",
                  link: "/dashboard/create"
                },
              ].map((item, idx) => (
                <Link key={idx} to={item.link} className="group">
                  <div className="bg-white p-8 rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all h-full flex flex-col">
                    <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center border border-slate-100 mb-5 text-slate-700 group-hover:bg-blue-50 group-hover:text-blue-600 group-hover:border-blue-100 transition-colors">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-slate-900">{item.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed flex-1">{item.desc}</p>
                    <div className="mt-6 flex items-center gap-2 text-sm font-medium text-blue-600 group-hover:gap-3 transition-all">
                      <span>{item.cta}</span>
                      <ArrowUpLeft className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-slate-50/50 border-t border-slate-100">
          <div className="container mx-auto px-6 lg:px-12 text-center max-w-2xl">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-tight mb-4">
              הצטרפו למהפכה.
            </h2>
            <p className="text-slate-500 text-base mb-10">
              גלו את הדור הבא של המערכות או שתפו את היצירה שלכם עם העולם.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/systems">
                <Button size="lg" className="gap-2 h-12 px-8 text-base font-medium rounded-xl bg-slate-900 hover:bg-slate-800 text-white shadow-none">
                  <Search className="h-4 w-4" />
                  לקטלוג
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="gap-2 h-12 px-8 text-base font-medium rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-900 shadow-none">
                  <Users className="h-4 w-4" />
                  הצטרפות בחינם
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </div>
    </Layout>
  );
};

export default About;